/**
 * VideoTutorialCard Component
 * 
 * Displays video tutorial preview cards
 * 
 * Version: 6.2.0
 */
import React from 'react'
import { Tutorial, DIFFICULTY_COLORS, CATEGORY_COLORS } from '../data/tutorials'
import './VideoTutorialCard.css'

interface VideoTutorialCardProps {
  tutorial: Tutorial
  onPlay?: (tutorial: Tutorial) => void
  showCategory?: boolean
  variant?: 'default' | 'compact' | 'featured'
  className?: string
}

const VideoTutorialCard: React.FC<VideoTutorialCardProps> = ({
  tutorial,
  onPlay,
  showCategory = true,
  variant = 'default',
  className = ''
}) => {
  const handlePlay = () => {
    onPlay?.(tutorial)
  }

  const getDifficultyLabel = (difficulty: Tutorial['difficulty']) => {
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1)
  }

  return (
    <div className={`video-tutorial-card ${variant} ${className}`}>
      {/* Thumbnail */}
      <div className="card-thumbnail" onClick={handlePlay}>
        <img 
          src={tutorial.thumbnail} 
          alt={tutorial.title}
          loading="lazy"
        />
        
        {/* Play Button Overlay */}
        <div className="play-overlay">
          <div className="play-button">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        </div>

        {/* Duration Badge */}
        <div className="duration-badge">⏱️ 
          {tutorial.duration}
        </div>

        {/* Featured Badge */}
        {tutorial.featured && (
          <div className="featured-badge">
            ⭐ Featured
          </div>
        )}

        {/* Difficulty Badge */}
        <div 
          className="difficulty-badge"
          style={{ backgroundColor: DIFFICULTY_COLORS[tutorial.difficulty] }}
        >
          {getDifficultyLabel(tutorial.difficulty)}
        </div>
      </div>

      {/* Content */}
      <div className="card-content">
        {/* Category Tag */}
        {showCategory && (
          <div 
            className="category-tag"
            style={{ 
              backgroundColor: `${CATEGORY_COLORS[tutorial.category]}20`,
              color: CATEGORY_COLORS[tutorial.category]
            }}
          >
            {`${tutorial.category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}`}
          </div>
        )}

        {/* Title */}
        <h3 className="card-title" onClick={handlePlay}>
          {tutorial.title}
        </h3>

        {/* Description */}
        <p className="card-description">
          {tutorial.description}
        </p>

        {/* Footer */}
        <div className="card-footer">
          {/* Tags */}
          <div className="card-tags">
            {tutorial.tags.slice(0, 3).map(tag => (
              <span key={tag} className="tag">
                #{tag}
              </span>
            ))}
          </div>

          {/* Watch Button */}
          <button className="watch-btn" onClick={handlePlay}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path d="M8 5v14l11-7z"/>
            </svg>
            Watch
          </button>
        </div>
      </div>
    </div>
  )
}

// Featured Card Variant
interface FeaturedTutorialCardProps {
  tutorial: Tutorial
  onPlay?: (tutorial: Tutorial) => void
  className?: string
}

export const FeaturedTutorialCard: React.FC<FeaturedTutorialCardProps> = ({
  tutorial,
  onPlay,
  className = ''
}) => {
  const handlePlay = () => {
    onPlay?.(tutorial)
  }

  return (
    <div className={`featured-tutorial-card ${className}`}>
      {/* Large Thumbnail */}
      <div className="featured-thumbnail" onClick={handlePlay}>
        <img 
          src={tutorial.thumbnail} 
          alt={tutorial.title}
        />
        <div className="featured-overlay">
          <div className="featured-play">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        </div>
        <div className="featured-duration">
          {tutorial.duration}
        </div>
      </div>

      {/* Featured Content */}
      <div className="featured-content">
        <div className="featured-label">
          <span className="featured-icon">⭐</span>
          Featured Tutorial
        </div>
        <h2 className="featured-title">{tutorial.title}</h2>
        <p className="featured-description">{tutorial.description}</p>
        <div className="featured-meta">
          <span 
            className="featured-difficulty"
            style={{ color: DIFFICULTY_COLORS[tutorial.difficulty] }}
          >
            {tutorial.difficulty}
          </span>
          <span className="meta-divider">•</span>
          <span className="featured-category">
            {`${tutorial.category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}`}
          </span>
        </div>
        <button className="featured-cta" onClick={handlePlay}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M8 5v14l11-7z"/>
          </svg>
          Watch Now
        </button>
      </div>
    </div>
  )
}

// Compact Card Variant
interface CompactTutorialCardProps {
  tutorial: Tutorial
  onPlay?: (tutorial: Tutorial) => void
  className?: string
}

export const CompactTutorialCard: React.FC<CompactTutorialCardProps> = ({
  tutorial,
  onPlay,
  className = ''
}) => {
  const handlePlay = () => {
    onPlay?.(tutorial)
  }

  return (
    <div className={`compact-tutorial-card ${className}`} onClick={handlePlay}>
      <img 
        src={tutorial.thumbnail} 
        alt={tutorial.title}
        className="compact-thumbnail"
      />
      <div className="compact-content">
        <span className="compact-title">{tutorial.title}</span>
        <span className="compact-duration">{tutorial.duration}</span>
      </div>
      <div className="compact-play">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z"/>
        </svg>
      </div>
    </div>
  )
}

export default VideoTutorialCard